import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Images, ChevronLeft, ChevronRight, Calendar, Download } from "lucide-react";
import { SponsorBanner } from "./SponsorBanner";
import { SponsorCard } from "./SponsorCard";
import { PremierAgentCard } from "./PremierAgentCard";
import { LISTING_STATUS_LABELS, CATEGORY_LABELS } from "./constants";

const HERO_IMAGE_ANIMATION = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.8 } };

export function HeroSection({ listing, ordered, hero, heroIndex, setHeroIndex, fileUrl, stats, onViewPhoto, onRequestShowing }) {
  return (
    <section className="relative sm:mt-5">
      <div className="relative h-[280px] sm:h-[62vh] lg:h-[500px] w-full overflow-hidden bg-[#111820] sm:rounded-none">
        <AnimatePresence mode="wait">
          {hero && (
            <motion.img key={hero.id} src={fileUrl(hero)} alt={listing.full_address || listing.project_name} onClick={() => onViewPhoto(heroIndex)}
              {...HERO_IMAGE_ANIMATION}
              className="absolute inset-0 h-full w-full object-cover cursor-pointer kb-zoom" data-testid="public-listing-hero-image" />
          )}
        </AnimatePresence>
        <div className="hidden sm:block absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
        {ordered.length > 1 && (
          <>
            <span data-testid="public-listing-hero-counter" className="sm:hidden absolute bottom-3 right-3 rounded bg-black/60 text-white text-xs font-mono px-2 py-1">{heroIndex + 1} / {ordered.length}</span>
            <button data-testid="public-listing-hero-prev" onClick={() => setHeroIndex((i) => (i - 1 + ordered.length) % ordered.length)} className="hidden sm:grid absolute left-4 top-8 h-10 w-10 rounded-full bg-black/30 hover:bg-black/50 place-items-center backdrop-blur-sm transition-colors text-white"><ChevronLeft size={18} /></button>
            <button data-testid="public-listing-hero-next" onClick={() => setHeroIndex((i) => (i + 1) % ordered.length)} className="hidden sm:grid absolute right-4 top-8 h-10 w-10 rounded-full bg-black/30 hover:bg-black/50 place-items-center backdrop-blur-sm transition-colors text-white"><ChevronRight size={18} /></button>
            <div data-testid="public-listing-hero-dots" className="hidden sm:flex absolute bottom-5 right-5 gap-1.5">
              {ordered.map((f, i) => <button key={f.id} aria-label={`Photo ${i + 1}`} onClick={() => setHeroIndex(i)} className={`h-1.5 rounded-full transition-all ${i === heroIndex ? "w-5 bg-[#C7A361]" : "w-1.5 bg-white/50 hover:bg-white/80"}`} />)}
            </div>
          </>
        )}
        {/* Desktop: text overlaid on the image, aligned to the shared content container */}
        <div className="hidden sm:block absolute inset-x-0 bottom-0 text-white pointer-events-none">
          <div className="mx-auto max-w-[1360px] px-6 lg:px-10 py-8 lg:py-10">
            {listing.listing_status && <span data-testid="public-listing-status-desktop" className="inline-block rounded-full bg-[#C7A361] text-[#111820] text-[11px] font-semibold uppercase tracking-[0.1em] px-3 py-1 mb-3">{LISTING_STATUS_LABELS[listing.listing_status] || listing.listing_status}</span>}
            <h1 data-testid="public-listing-title" className="font-serif-display text-4xl">{listing.address_street || listing.full_address || listing.project_name}</h1>
            {listing.address_city_state_zip && <p className="mt-1 text-base text-white/70">{listing.address_city_state_zip}</p>}
            <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
              {listing.price && <div data-testid="public-listing-price-desktop" className="font-serif-display text-2xl">{listing.price}</div>}
              {stats.map((s) => (
                <div key={s.label} data-testid={`public-listing-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`} className="flex items-center gap-1.5 text-base"><s.icon size={16} className="text-[#C7A361]" />{s.value} {s.label}</div>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap gap-3 pointer-events-auto">
              <button data-testid="public-listing-schedule-showing" onClick={onRequestShowing} className="inline-flex items-center gap-1.5 rounded-full bg-[#C7A361] text-[#111820] text-xs font-semibold uppercase tracking-[0.08em] px-5 py-2.5 hover:brightness-95 transition"><Calendar size={14} />Schedule a Showing</button>
              {ordered.length > 0 && <a href="#gallery" data-testid="public-listing-view-photos" className="inline-flex items-center gap-1.5 rounded-full bg-[#111820] border border-white/30 text-white text-xs font-semibold uppercase tracking-[0.08em] px-5 py-2.5 hover:bg-white/10 transition"><Images size={14} />View Gallery</a>}
            </div>
          </div>
        </div>
      </div>
      {/* Mobile: plain content below the image, price before address, single outlined CTA */}
      <div className="sm:hidden px-4 py-5">
        {listing.listing_status && <span data-testid="public-listing-status" className="block text-xs font-semibold uppercase tracking-[0.1em] text-[#C7A361] mb-1.5">{LISTING_STATUS_LABELS[listing.listing_status] || listing.listing_status}</span>}
        {listing.price && <div data-testid="public-listing-price" className="font-serif-display text-3xl text-[#202833]">{listing.price}</div>}
        <h1 data-testid="public-listing-title-mobile" className="mt-1 font-serif-display text-xl text-[#202833]">{listing.address_street || listing.full_address || listing.project_name}</h1>
        {listing.address_city_state_zip && <p className="text-sm text-[#667085]">{listing.address_city_state_zip}</p>}
        {stats.length > 0 && (
          <div className="mt-3 flex items-center gap-3 text-sm text-[#202833]">
            {stats.map((s, i) => (
              <span key={s.label} className={`flex items-center gap-1.5 ${i > 0 ? "border-l border-[#E7E4DE] pl-3" : ""}`}><s.icon size={15} className="text-[#667085]" />{s.value} {s.label}</span>
            ))}
          </div>
        )}
        {ordered.length > 0 && (
          <a href="#gallery" data-testid="public-listing-view-photos-mobile" className="mt-4 flex items-center justify-center gap-2 rounded-full border border-[#C7A361] text-[#202833] text-xs font-semibold uppercase tracking-[0.08em] py-3"><Images size={14} />View Photo Gallery</a>
        )}
      </div>
    </section>
  );
}

export function PhotosSection({ ordered, fileUrl, onViewPhoto, onDownloadAll }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? ordered : ordered.slice(0, 8);
  return (
    <div id="gallery" className="scroll-mt-24">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833]">Photo Gallery</h2>
        <div className="flex items-center flex-wrap gap-4">
          {ordered.length > 8 && (
            <button type="button" onClick={() => setShowAll((v) => !v)} data-testid="public-listing-view-all-photos" className="text-sm font-medium text-[#C7A361] hover:underline whitespace-nowrap">
              {showAll ? "Show Fewer Photos" : `View All ${ordered.length} Photos`}
            </button>
          )}
          {onDownloadAll && ordered.length > 0 && <button data-testid="public-listing-download-all" onClick={onDownloadAll} className="inline-flex items-center gap-1.5 rounded-full border border-[#C7A361] text-[#202833] text-xs font-semibold uppercase tracking-[0.08em] px-4 py-2 hover:bg-[#F8F7F4] transition whitespace-nowrap"><Download size={13} />Download All Photos</button>}
        </div>
      </div>
      {ordered.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {visible.map((file, i) => (
            <button key={file.id} data-testid={`public-listing-photo-${file.id}`} onClick={() => onViewPhoto(i)} className="aspect-square overflow-hidden rounded-lg bg-[#F8F7F4]">
              <img src={fileUrl(file)} alt="" loading="lazy" className="h-full w-full object-cover hover:scale-105 transition-transform duration-300" />
            </button>
          ))}
        </div>
      ) : <p className="text-sm text-[#667085]">Photos coming soon.</p>}
    </div>
  );
}

export function MortgageSection({ ads, onInquire, isMobile }) {
  if (!ads.length) return null;
  return (
    <div id="local-mortgage" className="scroll-mt-24">
      <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833]">Mortgage &amp; Financing</h2>
      <p className="mt-1 text-sm text-[#667085]">Get pre-qualified with a trusted local lender.</p>
      <div className={`mt-5 ${isMobile ? "space-y-4" : "grid grid-cols-1 sm:grid-cols-3 gap-4"}`}>
        {ads.slice(0, 3).map((ad) => <SponsorBanner key={ad.id} advertiser={ad} onInquire={onInquire} compact={!isMobile} />)}
      </div>
    </div>
  );
}

export function PremierAgentsSection({ ads, slug, withQS }) {
  if (!ads.length) return null;
  const shown = ads.slice(0, 6);
  return (
    <div id="local-agents" className="scroll-mt-24">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833]">Premier Real Estate Agents</h2>
          <p className="mt-1 text-sm text-[#667085]">Experienced local agents ready to help with your next move.</p>
        </div>
        {ads.length > shown.length && (
          <Link to={withQS(`/listing/${slug}/sponsors`)} data-testid="public-listing-view-all-agents" className="text-sm font-medium text-[#C7A361] hover:underline whitespace-nowrap">View All {ads.length} Premier Agents →</Link>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map((ad) => <PremierAgentCard key={ad.id} advertiser={ad} />)}
      </div>
    </div>
  );
}

const SERVICE_CATEGORIES = ["inspections", "cleaning", "moving", "junk_removal"];

export function LocalServicesSection({ sponsorsByCategory, slug, withQS, onInquire, isMobile }) {
  const groups = SERVICE_CATEGORIES.map((cat) => ({ cat, ads: (sponsorsByCategory[cat] || []).slice(0, 3) })).filter((g) => g.ads.length);
  if (!groups.length) return null;
  return (
    <div id="local-services" className="scroll-mt-24">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833]">Trusted Local Partners</h2>
          <p className="mt-1 text-sm text-[#667085]">Services for every step of your move.</p>
        </div>
        <Link to={withQS(`/listing/${slug}/sponsors`)} data-testid="public-listing-view-all-sponsors" className="text-sm font-medium text-[#C7A361] hover:underline whitespace-nowrap">View All Partners →</Link>
      </div>
      <div className={isMobile ? "space-y-8" : "space-y-8"}>
        {groups.map(({ cat, ads }) => (
          <div key={cat}>
            <h3 className="text-xs font-semibold uppercase tracking-[0.1em] text-[#98A2B3] mb-3">{CATEGORY_LABELS[cat]}</h3>
            <div className={isMobile ? "space-y-4" : "grid grid-cols-2 sm:grid-cols-3 gap-4"}>
              {ads.map((ad) => <SponsorCard key={ad.id} advertiser={ad} onInquire={onInquire} compact={!isMobile} outline={isMobile} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PromoSection({ contact, onRequestShowing }) {
  if (!contact?.name) return null;
  return (
    <section className="bg-[#F8F7F4] border-y border-[#E7E4DE] py-14 sm:py-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 text-center">
        <h2 className="font-serif-display text-2xl sm:text-3xl text-[#202833]">Thinking About Buying or Selling?</h2>
        <div className="mt-6 flex flex-col items-center gap-3">
          {contact.photo_url ? <img src={contact.photo_url} alt={contact.name} className="h-16 w-16 rounded-full object-cover" /> : <div className="h-16 w-16 rounded-full bg-white border border-[#E7E4DE] grid place-items-center font-serif-display text-xl text-[#667085]">{contact.name[0]}</div>}
          <p className="font-serif-display text-lg text-[#202833]">{contact.name}</p>
          {contact.company && <p className="text-sm text-[#667085]">{contact.company}</p>}
        </div>
        <button data-testid="public-listing-promo-cta" onClick={onRequestShowing} className="mt-6 rounded-full bg-[#C7A361] text-[#111820] text-sm font-semibold px-6 py-3 hover:brightness-95 transition">Get in Touch</button>
      </div>
    </section>
  );
}


export function SponsorBannerSlot({ advertiser, onInquire, className, compact }) {
  if (!advertiser) return null;
  return <div className={className}><SponsorBanner advertiser={advertiser} onInquire={onInquire} compact={compact} /></div>;
}
